import * as S from "./styles.ts";
import { PopupItem } from "./styles.ts";
import { useSlate } from "slate-react";
import {
  blockOptions,
  formattingOptions,
  isBlockActive,
  isMarkActive,
  toggleBlock,
  toggleMark,
} from "@admin/config/overrides/fieldTypes/MarkdownEditor/helpers";
import { Button, Input, Popup } from "@template/components";
import ChevronDown from "@template/icons/ChevronDown.tsx";
import { Link } from "lucide-react";
import { Editor, Range, Text, Transforms } from "slate";
import { useEffect, useRef, useState } from "react";

const ToolbarButton = ({
  option,
  disabled,
}: {
  option: {
    label: string;
    key: string;
    icon: any;
  };
  disabled?: boolean;
}) => {
  const slate = useSlate();
  const isMark = formattingOptions.some(({ key }) => key === option.key);
  const isBlock = blockOptions.some(({ key }) => key === option.key);

  const isActive = isMark
    ? isMarkActive(slate, option.key)
    : isBlockActive(
        slate,
        option.key as
          | "ol"
          | "ul"
          | "paragraph"
          | "heading-one"
          | "heading-two"
          | "heading-three"
          | "heading-four"
      );

  const onToggle = (e: any) => {
    e.preventDefault();
    if (isMark) {
      toggleMark(slate, option.key);
    } else if (isBlock) {
      toggleBlock(
        slate,
        option.key as
          | "ol"
          | "ul"
          | "paragraph"
          | "heading-one"
          | "heading-two"
          | "heading-three"
          | "heading-four"
      );
    }
  };

  return (
    <S.ButtonWrapper $isActive={isActive}>
      <S.Button
        key={option.key}
        onClick={onToggle}
        $disabled={disabled}
      >
        {option.icon}
      </S.Button>
    </S.ButtonWrapper>
  );
};

const insertVariantWord = (editor: Editor, us: string, uk: string) => {
  if (!uk && !us) return;
  const textToShow = uk || us;

  Editor.withoutNormalizing(editor, () => {
    // 0) collapse any selection
    if (editor.selection && !Range.isCollapsed(editor.selection)) {
      Transforms.collapse(editor, { edge: "end" });
    }

    // 1) turn on marks and insert the variant word
    Editor.addMark(editor, "variant", true);
    Editor.addMark(editor, "uk", uk);
    Editor.addMark(editor, "us", us);
    Editor.insertText(editor, textToShow);

    // 2) split here to end the marked leaf
    Transforms.splitNodes(editor, { match: Text.isText, always: true });

    // 3) clear the active marks so future typing isn’t variant
    Editor.removeMark(editor, "variant");
    Editor.removeMark(editor, "us");
    Editor.removeMark(editor, "uk");

    // 4) insert a space in the NEW (unmarked) leaf
    Editor.insertText(editor, " ");

    // 5) make sure the DOM/selection are synced
    setTimeout(() => {
      const after = Editor.after(editor, editor.selection?.focus ?? [], {
        unit: "offset",
      });
      if (after) Transforms.select(editor, after);
    }, 0);
  });
};

const VariantWordButton = ({ onClose }: { onClose?: (e?: any) => void }) => {
  const editor = useSlate();
  const savedSelection = useRef(editor.selection);

  // detect if editing existing variant
  const [match] = Editor.nodes(editor, {
    match: (n) => (n as any).variant === true,
    universal: true,
  });
  const existingNode: any = match ? match[0] : null;
  const isEditing = !!existingNode;

  const [usWord, setUsWord] = useState<string>(existingNode?.us || "");
  const [ukWord, setUkWord] = useState<string>(existingNode?.uk || "");

  const handleApply = () => {
    if (!usWord && !ukWord) return;

    if (isEditing && savedSelection.current) {
      // Update existing variant node in place
      const textToShow = ukWord || usWord;
      Editor.withoutNormalizing(editor, () => {
        Transforms.setNodes(editor, { us: usWord, uk: ukWord } as any, {
          at: savedSelection.current,
          match: (n) => Text.isText(n) && (n as any).variant === true,
        });
        // Also update the displayed text if it changed
        const [entry] = Editor.nodes(editor, {
          at: savedSelection.current,
          match: (n) => Text.isText(n) && (n as any).variant === true,
        });
        if (entry) {
          const [node, path] = entry;
          const currentText = (node as any).text;
          if (currentText !== textToShow) {
            Transforms.insertText(editor, textToShow, {
              at: {
                anchor: { path, offset: 0 },
                focus: { path, offset: currentText.length },
              },
            });
          }
        }
      });
    } else {
      // Insert new variant word
      insertVariantWord(editor, usWord, ukWord);
    }
    onClose();
  };

  return (
    <>
      <Input
        type="text"
        label="US Word"
        fullWidth
        autoFocus
        value={usWord}
        onChange={setUsWord}
      />
      <Input
        type="text"
        label="UK Word"
        fullWidth
        value={ukWord}
        onChange={setUkWord}
      />
      <Button
        size="sm"
        onClick={handleApply}
      >
        {isEditing ? "Update" : "Insert"}
      </Button>
    </>
  );
};

const LinkEditor = () => {
  const slate = useSlate();
  const savedSelection = useRef(slate.selection);

  const existingLink = getActiveLink(slate);

  const [urlValue, setUrlValue] = useState(existingLink?.url || "");
  const [titleValue, setTitleValue] = useState(existingLink?.title || "");

  const applyLink = (newUrl: string, newTitle: string) => {
    if (!savedSelection.current) return;

    Transforms.setNodes(
      slate,
      { ...existingLink, url: newUrl, title: newTitle },
      {
        at: savedSelection.current,
        match: (n) => Text.isText(n) && (n as any).link === true,
        split: true,
      }
    );
  };

  return (
    <>
      <Input
        type="text"
        label="URL"
        placeholder="/phone-numbers"
        fullWidth
        autoFocus
        value={urlValue}
        onChange={(val) => {
          setUrlValue(val);
          applyLink(val, titleValue);
        }}
      />
      <Input
        type="text"
        label="Title"
        placeholder="Click to go to phone numbers"
        fullWidth
        value={titleValue}
        onChange={(val) => {
          setTitleValue(val);
          applyLink(urlValue, val);
        }}
      />
    </>
  );
};

const getActiveLink = (editor) => {
  const { selection } = editor;
  if (!selection) return null;

  // Prefer active marks (current typing context)
  const marks = (Editor as any).marks(editor);
  if (marks?.link) return marks;

  // Otherwise, find text node with link mark in the selection
  const [entry] = Editor.nodes(editor, {
    at: selection,
    match: (n) => Text.isText(n) && (n as any).link === true,
    universal: true,
  });
  return entry ? (entry[0] as any) : null;
};

const Toolbar = ({ disabled }) => {
  const editor = useSlate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Wait one tick for Slate to fully initialize
    setTimeout(() => setReady(true), 0);
  }, []);

  const activeBlock = ready
    ? blockOptions.find((b) =>
        isBlockActive(
          editor,
          b.key as
            | "paragraph"
            | "ol"
            | "ul"
            | "heading-one"
            | "heading-two"
            | "heading-three"
            | "heading-four"
        )
      )
    : null;

  const linkIsActive = ready ? isMarkActive(editor, "link") : false;
  const isVariantActive = ready ? isMarkActive(editor, "variant") : false;
  const [linkPopupOpen, setLinkPopupOpen] = useState(false);

  // Sync popup with mark state — open when link mark becomes active
  useEffect(() => {
    if (linkIsActive) {
      setLinkPopupOpen(true);
    }
  }, [linkIsActive]);

  const handleLinkToggle = (e: any) => {
    e.preventDefault();
    if (linkPopupOpen) {
      setLinkPopupOpen(false);
    } else {
      toggleMark(editor, "link");
      setLinkPopupOpen(true);
    }
  };

  return (
    <S.ToolbarContainer>
      <S.ButtonsContainer>
        {[
          ...formattingOptions.filter(
            ({ key }) => key !== "link" && key !== "variant"
          ),
        ].map((option) => (
          <ToolbarButton
            option={option}
            key={option.key}
            disabled={disabled}
          />
        ))}
        <Popup
          isOpen={linkPopupOpen}
          onOpenChange={setLinkPopupOpen}
          triggerProps={{
            // @ts-ignore
            style: {
              flex: 1,
              borderRight: "1px solid #424c61",
            },
          }}
          width={300}
          popupContent={<LinkEditor />}
        >
          <S.ButtonWrapper $isActive={linkIsActive}>
            <S.Button onClick={handleLinkToggle}>
              <Link size={16} />
            </S.Button>
          </S.ButtonWrapper>
        </Popup>
        <Popup
          isOpen={isVariantActive}
          triggerProps={{
            // @ts-ignore
            style: {
              flex: 1,
              borderRight: "1px solid #424c61",
            },
          }}
          popupContent={
            <VariantWordButton
              onClose={() => Editor.removeMark(editor, "variant")}
            />
          }
        >
          <ToolbarButton
            option={formattingOptions.find(({ key }) => key === "variant")}
          />
        </Popup>
        <Popup
          triggerProps={{
            // @ts-ignore
            style: {
              flex: 1,
            },
          }}
          popupContent={
            <S.PopupContainer>
              {blockOptions.map((option) => (
                <PopupItem key={option.key}>
                  <ToolbarButton option={option} />
                  {option.label}
                </PopupItem>
              ))}
            </S.PopupContainer>
          }
        >
          <ToolbarButton
            option={{
              label: "yo",
              key: "extra",
              icon: (
                <>
                  {activeBlock?.icon}
                  <ChevronDown />
                </>
              ),
            }}
          />
        </Popup>
      </S.ButtonsContainer>
    </S.ToolbarContainer>
  );
};

export default Toolbar;
